const SELECTORS = {
    block: '.wp-block-ssm-accordion',
    item: '.wp-block-ssm-accordion__item',
    trigger: '[data-accordion-trigger]',
    panel: '[data-accordion-panel]',
};

const CLASSES = { open: 'is-open' };

const setItemState = (item, isOpen, triggerMap, panelMap) => {
    const trigger = triggerMap.get(item);
    const panel = panelMap.get(item);

    item.classList.toggle(CLASSES.open, isOpen);
    item.dataset.open = String(isOpen);

    if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
    if (panel) {
        panel.setAttribute('aria-hidden', String(!isOpen));
        panel.inert = !isOpen;
    }
};

const focusTriggerAt = (triggers, index) => {
    const len = triggers.length;
    if (!len) return;
    triggers[((index % len) + len) % len]?.focus();
};

export const initAccordion = (block) => {
    if (!block || block.dataset.accordionReady) return null;

    const items = Array.from(block.querySelectorAll(SELECTORS.item));
    if (!items.length) return null;

    const allowMultiple = block.dataset.allowMultiple === 'true';
    const openFirst = block.dataset.openFirst === 'true';

    block.dataset.accordionReady = 'true';

    const triggerMap = new Map();
    const panelMap = new Map();

    items.forEach((item) => {
        const trigger = item.querySelector(SELECTORS.trigger);
        const panel = item.querySelector(SELECTORS.panel);
        if (trigger) triggerMap.set(item, trigger);
        if (panel) panelMap.set(item, panel);
    });

    const triggers = items.map((item) => triggerMap.get(item)).filter(Boolean);
    const cleanupHandlers = [];

    const closeSiblings = (current) => {
        if (allowMultiple) return;
        items.forEach((item) => {
            if (item !== current) setItemState(item, false, triggerMap, panelMap);
        });
    };

    const toggleItem = (item) => {
        const isOpen = item.classList.contains(CLASSES.open);
        if (!isOpen) closeSiblings(item);
        setItemState(item, !isOpen, triggerMap, panelMap);
    };

    const handleKeydown = (e) => {
        const index = triggers.indexOf(e.currentTarget);
        if (index === -1) return;

        const actions = {
            ArrowDown: () => focusTriggerAt(triggers, index + 1),
            ArrowUp: () => focusTriggerAt(triggers, index - 1),
            Home: () => focusTriggerAt(triggers, 0),
            End: () => focusTriggerAt(triggers, triggers.length - 1),
        };

        if (actions[e.key]) {
            e.preventDefault();
            actions[e.key]();
        }
    };

    items.forEach((item, index) => {
        const trigger = triggerMap.get(item);
        const shouldOpen = item.dataset.open === 'true' || item.classList.contains(CLASSES.open) || (openFirst && index === 0);

        setItemState(item, shouldOpen, triggerMap, panelMap);

        if (trigger) {
            const onClick = () => toggleItem(item);
            trigger.addEventListener('click', onClick);
            trigger.addEventListener('keydown', handleKeydown);
            cleanupHandlers.push(() => {
                trigger.removeEventListener('click', onClick);
                trigger.removeEventListener('keydown', handleKeydown);
            });
        }
    });

    if (!allowMultiple) {
        const openItems = items.filter((item) => item.classList.contains(CLASSES.open));
        if (openItems.length > 1) {
            openItems.slice(1).forEach((item) => setItemState(item, false, triggerMap, panelMap));
        }
    }

    return {
        destroy() {
            cleanupHandlers.forEach((fn) => fn());

            items.forEach((item) => {
                item.classList.remove(CLASSES.open);
                const panel = panelMap.get(item);
                if (panel) {
                    panel.setAttribute('aria-hidden', 'true');
                    panel.inert = false;
                }
                const trigger = triggerMap.get(item);
                if (trigger) trigger.setAttribute('aria-expanded', 'false');
            });

            delete block.dataset.accordionReady;
        },
    };
};

export const Accordion = () => {
    document.querySelectorAll(SELECTORS.block).forEach(initAccordion);
};
